import { Button, Container, Paper, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { Controller, useForm } from "react-hook-form";
import { Select, TextInput } from "react-hook-form-mantine";

import {
  usePatchEvent,
  useCheckEventSlugAvailability,
} from "../../../api/event";
import { reduceWhiteSpaces } from "../../../services/helper";
import resolver from "@congrs/resolver";
import { SLUG_PATTERN_STR, web_editEventSchema } from "@congrs/schemas";
import FeatureFlagsSelector from "../FeatureFlagsSelector";

const SLUG_FORMAT_RE = new RegExp(SLUG_PATTERN_STR);

export default function EditForm({ data, isSysAdmin, enabledFlags }) {
  const { id, name } = data;
  const { mutate, isPending: isLoading } = usePatchEvent(id);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      name: data.name,
      status: data.status,
      autoCloseDays: data.autoCloseDays,
      enabledFeatures: enabledFlags,
      attendeeLimit: data.attendeeLimit,
      slug: data.slug || "",
    },
    resolver: resolver(web_editEventSchema),
  });

  const slugValue = watch("slug");
  const [debouncedSlug] = useDebouncedValue(slugValue?.trim() || "", 300);
  // An unchanged slug is accepted as-is; events still on the numeric backfill
  // would otherwise show an error on a field the user never touched.
  const slugFormatValid =
    !debouncedSlug ||
    debouncedSlug === data?.slug ||
    SLUG_FORMAT_RE.test(debouncedSlug);
  const isCheckingAvailability =
    isSysAdmin &&
    debouncedSlug &&
    debouncedSlug !== data?.slug &&
    slugFormatValid;

  const { data: slugAvailability, isFetching: isSlugAvailabilityFetching } =
    useCheckEventSlugAvailability({
      slug: isCheckingAvailability ? debouncedSlug : "",
      excludeEventId: id,
    });

  let slugError = null;
  if (!slugValue?.trim()) {
    slugError = "Slug is required";
  } else if (debouncedSlug && !slugFormatValid) {
    slugError = "Only lowercase letters, numbers, and dashes";
  } else if (
    isCheckingAvailability &&
    !isSlugAvailabilityFetching &&
    slugAvailability &&
    slugAvailability.available === false
  ) {
    slugError = "This slug is already in use";
  }

  // If user is not a system admin,
  // disable previous status, because they can
  // update status in order 'draft' -> 'active' -> 'closed'
  const statusOptions = [
    {
      label: "Draft",
      value: "draft",
      disabled: !isSysAdmin && data.status !== "draft",
    },
    {
      label: "Active",
      value: "active",
      disabled:
        !isSysAdmin && (data.status !== "active" || data.status === "closed"),
    },
    { label: "Closed", value: "closed" },
  ];

  const onSubmit = (data) => {
    data.name = reduceWhiteSpaces(data.name);
    mutate(data, {
      onSuccess: () => {
        reset(data);
      },
    });
  };

  return (
    <Container size={500}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            label="Name"
            type="text"
            name="name"
            defaultValue={name}
            control={control}
            withAsterisk
            mt="md"
            disabled={!isSysAdmin}
          />

          <Select
            label="Status"
            placeholder="Select status"
            name="status"
            data={statusOptions}
            control={control}
            mt="md"
            allowDeselect={false}
          />

          <TextInput
            label="Public URL slug"
            name="slug"
            control={control}
            mt="md"
            placeholder="my-event-2026"
            description="Used in public links. Lowercase letters, numbers, and dashes only."
            disabled={!isSysAdmin}
            error={slugError || undefined}
            rightSection={
              isSlugAvailabilityFetching ? (
                <Text size="xs" c="dimmed">
                  ...
                </Text>
              ) : null
            }
          />

          <Controller
            name="enabledFeatures"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FeatureFlagsSelector
                value={value}
                onChange={onChange}
                control={control}
                disabled={!isSysAdmin}
              />
            )}
          />

          {!isSysAdmin && (
            <Text size="md" c="red" mt="md">
              Only system admins can edit events!
            </Text>
          )}
          <Button
            type="submit"
            mt="md"
            loading={isLoading}
            disabled={
              !isSysAdmin ||
              !isDirty ||
              !!slugError ||
              isSlugAvailabilityFetching
            }
          >
            Edit event
          </Button>
        </form>
      </Paper>
    </Container>
  );
}
